// ===== Business Documents Inbox View =====
// #view-business-documents SPA 뷰 init 모듈 (문서함 셸).
// router.registerViewEnter()로 view-enter 디스패처에 등록하고, 최초 진입 시 1회 목록을 조회한다.
// 셸(Phase 1) 위에 행 투영·정렬·상태 어휘(Phase 2), 상세 패널·범위 필터(Phase 3)를 얹었다.
//
// 목록 패널 상태는 넷이다: loading / empty / list / error. 동시에 둘이 보이면 안 된다.
// 로딩을 빼면 "전부 hidden + 0건" 백지가 되어 0건·장애·로딩이 한 화면으로 붕괴한다.
// 상세 패널도 같은 배타 계약을 따른다: hint / loading / content / error.

(function () {
  type BusinessDocumentRecord = Record<string, unknown>;
  type PanelState = "loading" | "empty" | "list" | "error";
  type DetailState = "hint" | "loading" | "content" | "error";

  // 문서번호가 없을 때 그 부재가 무엇을 뜻하는지 — 상태마다 다르다.
  //   expected 아직 승인 전이라 번호가 없는 게 설계된 정상이다
  //   pending  승인은 됐고 발행(채번)을 기다린다
  //   anomaly  발행됐는데 번호가 없다 (contracts.ts:368-373 norms 위반 조합)
  type UnassignedKind = "expected" | "pending" | "anomaly";

  interface UnassignedNumberPolicy {
    kind: UnassignedKind;
    text: string;
  }

  interface DocumentRow {
    id: string;
    type: string;
    typeLabel: string;
    stateKey: string;
    stateLabel: string;
    title: string;
    number: string;
    numberKind: UnassignedKind | null;
    counterparty: string;
    amount: string;
    date: string;
  }

  // 첫 진입 1회만 초기화한다. Promise 를 들고 있어야 두 번째 진입이 "이미 끝난 척" 하지 않는다 —
  // 초기화는 인증 확인 -> 범위 결정 -> 목록 조회의 다단계라 완료 시점이 동기가 아니다.
  var _initPromise: Promise<void> | null = null;

  // ===== API 상수 =====
  var _API_LIST = "/api/business-documents/";
  var _API_ME = "/api/auth/me";

  // ===== 범위 필터 상태 =====
  // 서버 목록은 평면 가시로 무필터 반환이다(routes-draft.ts:71-75, 사용자 결정 2026-08-21).
  // 그 계약을 바꾸지 않는다 — "내 문서" 는 이미 받은 배열에 대한 클라이언트 투영이고,
  // 범위를 바꿔도 서버 왕복이 늘지 않는다.
  var _VIEW_SCOPE_ID = "business-documents";
  var _SCOPE_STORAGE_KEY = "scope-switch:" + _VIEW_SCOPE_ID;
  var _CORE_SCOPES = ["team", "personal"];

  // 위젯(scope-switch.ts:31-44)의 기본은 전체(team)지만 문서함의 기본은 내 문서다.
  // 오너십은 벽이 아니라 필터이므로 전체 문서 토글은 항상 옆에 남는다.
  var _scopes: string[] = ["personal"];

  var _documents: BusinessDocumentRecord[] = [];
  var _rowElements: HTMLElement[] = [];
  var _selectedId: string | null = null;
  // 조회가 실패한 화면에서 범위만 바꿨을 때 오류 배너를 0건 화면으로 덮지 않기 위한 표식.
  var _loadFailed = false;

  // ===== 문구 =====
  // 목록은 all-or-nothing 이다: 레코드 1건의 parse 실패가 목록 전체를 끊고 400 으로 온다
  // (lib/business-documents/document-store.ts:136-143). 400 이라고 클라이언트 잘못이 아니므로
  // 원인별로 문구를 갈라야 추적이 된다.
  var _MSG_LOADING = "문서 목록을 불러오는 중입니다.";
  var _MSG_ERROR_DATA = "문서 데이터에 문제가 있습니다. 관리자 확인이 필요합니다.";
  var _MSG_ERROR_FETCH = "문서 목록을 불러오지 못했습니다.";

  // 값이 없는 칸의 표기. 빈 문자열로 두면 칸이 사라져 "없음" 과 "안 보임" 이 구분되지 않는다.
  var _EMPTY_VALUE = "—";

  // 0건의 원인은 둘이다: 정말 없거나, 범위 필터가 걸렸거나.
  // 구분하지 않으면 사용자는 필터가 켜진 줄 모른 채 "문서가 없다" 로 읽는다.
  var _EMPTY_ALL_TITLE = "아직 만들어진 문서가 없습니다";
  var _EMPTY_ALL_SUB = "채팅에서 /contract 또는 /견적서 로 시작하세요.";
  var _EMPTY_MINE_TITLE = "내가 만든 문서가 없습니다";
  var _EMPTY_MINE_SUB = '전체 문서를 보려면 범위를 "전체 문서" 로 바꾸세요.';

  // ===== 어휘 =====
  // BUSINESS_DOCUMENT_STATES(lib/business-documents/contracts.ts:48) 6종을 빠짐없이 덮는다.
  var _STATE_LABELS: Record<string, string> = {
    draft: "초안",
    needs_input: "입력 필요",
    review_pending: "승인 대기",
    approved: "승인됨",
    rendered: "발행됨",
    void: "무효",
  };

  // BUSINESS_DOCUMENT_TYPES(contracts.ts:47) 2종.
  var _TYPE_LABELS: Record<string, string> = {
    contract: "계약서",
    quotation: "견적서",
  };

  // BUSINESS_DOCUMENT_NUMBER_KEY_BY_TYPE(contracts.ts:453)의 사본이다.
  // 문서번호는 봉투가 아니라 본문 키에 있다 — 봉투에서 찾으면 전 문서가 조용히 빈 값이 된다.
  // 브라우저 classic script 는 서버 모듈을 require 할 수 없어 사본이 불가피하므로,
  // test/business-documents-number-key-contract.test.ts 가 두 소스를 읽어 동치를 고정한다.
  var _NUMBER_KEY_BY_TYPE: Record<string, string> = {
    quotation: "documentNumber",
    contract: "contractNumber",
  };

  // 번호 부재의 의미는 상태마다 다르다. 같은 라벨로 그리면 "설계된 정상"과 "곧 닫혀야 할 창"과
  // "발행 사고"가 한 표기로 붕괴한다. 근거: decisions.md:15/:40(승인 시 채번),
  // contracts.ts:484/:592-598(동결 후 numberKey 만 write-once), :368-373(번호 없는 산출물 = norms 위반).
  // 문구는 전 상태 "발행 시 채번" 통일(2026-08-30) — 실제 채번은 발행 전이에서 일어난다.
  // "승인 후 채번" 은 한 걸음 이른 거짓 약속이었다(카드 쪽 표와 값 동등 게이트 공유).
  var _UNASSIGNED_NUMBER: Record<string, UnassignedNumberPolicy> = {
    draft: { kind: "expected", text: "발행 시 채번" },
    needs_input: { kind: "expected", text: "발행 시 채번" },
    review_pending: { kind: "expected", text: "발행 시 채번" },
    approved: { kind: "pending", text: "발행 시 채번" },
    rendered: { kind: "anomaly", text: "번호 없음" },
    void: { kind: "expected", text: _EMPTY_VALUE },
  };

  // 6개 밖의 상태(도달 불가 방어)는 판단을 유보한다 — 정상도 사고도 주장하지 않는다.
  var _UNASSIGNED_FALLBACK: UnassignedNumberPolicy = { kind: "expected", text: _EMPTY_VALUE };

  // SourceRefSchema.sourceType(lib/ax-contracts/index.ts:22) 6종. 나머지 표면이 전부 한국어
  // 라벨이라 출처만 원시 enum 으로 두면 같은 화면 안에서 어휘가 갈라진다.
  var _SOURCE_TYPE_LABELS: Record<string, string> = {
    chat: "채팅",
    "vault-note": "노트",
    file: "파일",
    web: "웹",
    "tool-output": "도구 출력",
    other: "기타",
  };

  // 발행 법인 id -> 정식 법인명. lib/business-documents/entity-registry.ts 의 ISSUER_ENTITIES
  // 프런트 사본이다 — 발행 법인 목록을 노출하는 REST 가 하나도 없어 브라우저 classic script 가
  // 권위를 읽을 방법이 없다. 그래서 phase-04 검증블록의 값 드리프트 게이트가 권위를 순회해
  // 이 표를 묶는다(사본을 순회하면 사본이 비었을 때 0회로 통과한다).
  // 키를 따옴표로 적는 것은 스타일이 아니라 게이트 계약이다: 드리프트 게이트가
  // JSON.stringify(entity.id) 로 대조하므로, 맨 식별자로 두면 그 대조가 어긋난다.
  var _ISSUER_LEGAL_NAMES: Record<string, string> = {
    "bigtion": "주식회사 빅션",
  };

  // 산출물 링크 문구. 발행본은 같은 바이트의 두 표현(PDF/HTML)이라 둘 다 건다.
  var _LABEL_OUTPUT_PDF = "PDF 열기";
  var _LABEL_OUTPUT_HTML = "HTML 열기";

  // ===== 유틸 =====

  function _isRecord(value: unknown): value is BusinessDocumentRecord {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function _documentsFromUnknown(payload: unknown): BusinessDocumentRecord[] {
    if (!_isRecord(payload)) throw new Error("Invalid business documents payload");
    const documents = payload.documents;
    if (!Array.isArray(documents) || !documents.every(_isRecord)) {
      throw new Error("Invalid business documents payload");
    }
    return documents;
  }

  function _byId(id: string): HTMLElement | null {
    if (typeof document === "undefined") return null;
    return document.getElementById(id);
  }

  function _setHidden(element: HTMLElement | null, hidden: boolean) {
    if (element) element.hidden = hidden;
  }

  function _setText(element: HTMLElement | null, text: string) {
    if (element) element.textContent = text;
  }

  // 상태 라이브 리전 — 스크린리더에 로딩/도착/실패를 통보한다.
  function _announce(text: string) {
    _setText(_byId("business-documents-status"), text);
  }

  // ===== 범위 필터 =====
  // 필터는 클라이언트 편의다. 서버에 scope/createdBy 쿼리를 보내는 순간 평면 가시 결정을
  // 뒤집는 것이고, 나중에 전체 보기를 만들 때 그 필터를 다시 걷어내야 한다.

  // 현재 액터 id (`user:<slug>`). 선례: public/js/core/room-list.ts:329.
  function _actorId(): string | null {
    if (typeof window === "undefined") return null;
    var actor = window.BigtionAuth?.actorContext?.actor?.actor;
    return typeof actor === "string" && actor !== "" ? actor : null;
  }

  function _isPersonalScope(): boolean {
    return _scopes.indexOf("personal") >= 0;
  }

  // proposal-review.ts:138-156 을 반대 기본값으로 미러링한다. 업무는 전체가 기본이지만
  // 문서함은 내 것이 기본이다 — 혼자 쓸 때 남의 문서에 파묻히지 않는 유일한 배치다.
  function _readPersistedDocumentScopes(personalEnabled: boolean): string[] {
    var scopes = ["personal"];
    try {
      var raw = localStorage.getItem(_SCOPE_STORAGE_KEY);
      if (raw) {
        var data: unknown = JSON.parse(raw);
        var rawScopes: unknown[] = _isRecord(data) && Array.isArray(data.scopes) ? data.scopes : [];
        var saved = rawScopes.filter(function (scope: unknown): scope is string {
          return typeof scope === "string" && _CORE_SCOPES.indexOf(scope) >= 0;
        });
        // 레거시 병합값(둘 다 저장)은 기본값인 내 문서로 수렴한다 — 단일선택 위젯이다.
        if (saved.indexOf("team") >= 0 && saved.indexOf("personal") < 0) scopes = ["team"];
      }
    } catch {
      // 저장값 파싱 실패 — 기본값(내 문서) 유지
    }
    // 신원을 모르면 "내 문서" 가 의미를 잃는다.
    if (!personalEnabled) scopes = ["team"];
    return scopes;
  }

  // ScopeSwitch.create 에는 초기값 인자가 없고 내부 _load() 는 저장값이 없으면 전체(team)로
  // 접힌다(scope-switch.ts:31-44, :69). 저장값이 없을 때 personal 을 1회 적어두지 않으면
  // 첫 렌더에서 위젯 칩은 "전체 문서" 를 켜고 목록은 내 문서만 보여주는 어긋남이 생긴다.
  function _seedDefaultScope(): void {
    try {
      if (localStorage.getItem(_SCOPE_STORAGE_KEY)) return;
      localStorage.setItem(_SCOPE_STORAGE_KEY, JSON.stringify({ scopes: ["personal"] }));
    } catch {
      // localStorage 불가 환경 — 지속만 실패하고 뷰 상태는 그대로 유지된다
    }
  }

  function _visibleDocuments(): BusinessDocumentRecord[] {
    if (!_isPersonalScope()) return _documents;
    var me = _actorId();
    // 신원을 모르면 숨기지 않는다. 여기서 걸러버리면 로그인 전 화면이 "문서 0건" 으로 오인된다.
    if (me === null) return _documents;
    return _documents.filter(function (doc) {
      return _isRecord(doc.createdBy) && doc.createdBy.actor === me;
    });
  }

  // ===== 행 투영 =====

  function _createdAtOf(doc: BusinessDocumentRecord): string {
    return typeof doc.createdAt === "string" ? doc.createdAt : "";
  }

  function _idOf(doc: BusinessDocumentRecord): string {
    return typeof doc.id === "string" ? doc.id : "";
  }

  // 서버 list() 는 readdirSync 결과를 정렬하지 않는다(document-store.ts:136-143).
  // id 가 `<type>-<YYYYMMDD>-<hex>` 라 그대로 두면 사전순 = 유형별로 묶인 순서가 나오고,
  // 패널 부제의 "최근 만들어진 순서" 약속이 깨진다. 서버 계약을 안 건드리고 여기서 정렬한다.
  function _sortDocuments(documents: BusinessDocumentRecord[]): BusinessDocumentRecord[] {
    return documents.slice().sort(function (a, b) {
      var leftAt = _createdAtOf(a);
      var rightAt = _createdAtOf(b);
      // ISO 8601 은 고정폭이라 문자열 비교가 곧 시간 비교다.
      if (leftAt !== rightAt) return leftAt < rightAt ? 1 : -1;
      // 동률은 id 사전순으로 고정한다 — 재조회마다 순서가 흔들리면 목록이 읽히지 않는다.
      var leftId = _idOf(a);
      var rightId = _idOf(b);
      if (leftId === rightId) return 0;
      return leftId < rightId ? -1 : 1;
    });
  }

  function _projectDocumentRow(doc: BusinessDocumentRecord): DocumentRow {
    var rawType = typeof doc.documentType === "string" ? doc.documentType : "";
    var rawState = typeof doc.state === "string" ? doc.state : "";
    // 도달성: state 는 z.enum 6종이고 list() 가 전건 parse 한다(contracts.ts:49-56).
    // API 경유로는 도달 불가한 방어다 — 미지 상태는 변형 없이 원문만 보여주고 크래시하지 않는다.
    var stateKey = Object.prototype.hasOwnProperty.call(_STATE_LABELS, rawState) ? rawState : "";
    var body = _isRecord(doc.body) ? doc.body : {};
    var isQuotation = rawType === "quotation";

    var rawNumber = body[_NUMBER_KEY_BY_TYPE[rawType]];
    var hasNumber = typeof rawNumber === "string" && rawNumber !== "";
    var unassigned = _UNASSIGNED_NUMBER[stateKey] || _UNASSIGNED_FALLBACK;

    // 저장값을 그대로 표시한다. 산식의 SSOT 는 lib/business-documents/amount-rules.ts 이고,
    // 프런트가 다시 계산하면 두 번째 진실이 생긴다.
    // truthiness 로 분기하지 않는다: MoneySchema(contracts.ts:150)에 .min(0) 이 없어 견적
    // totalAmount(:222)는 0 과 음수가 유효하다. 0원(면세·무상 공급)과 미입력은 승인 판단이
    // 갈리는 서로 다른 사실이라 한 표기로 접으면 안 된다.
    var rawAmount = isQuotation ? body.totalAmount : body.contractAmount;

    // 본문 날짜는 YYYY-MM-DD, 봉투 createdAt 은 ISO 8601 이라 앞 10자가 같은 모양이다.
    // 로케일 날짜 포맷터는 실행 환경 로케일에 묶여 테스트까지 환경 의존으로 만든다 — 쓰지 않는다.
    var rawDate = isQuotation ? body.issueDate : body.contractDate;
    var fallbackDate = _createdAtOf(doc).slice(0, 10);

    // 도달성: counterparty 는 스키마상 필수이고 displayName 은 min(1)(contracts.ts:81) —
    // 대체표기는 스키마 밖 입력에 대한 방어다.
    var counterparty = _isRecord(doc.counterparty) && typeof doc.counterparty.displayName === "string"
      ? doc.counterparty.displayName
      : _EMPTY_VALUE;

    return {
      id: _idOf(doc),
      type: rawType,
      typeLabel: _TYPE_LABELS[rawType] || rawType,
      stateKey: stateKey,
      stateLabel: _STATE_LABELS[stateKey] || rawState,
      title: typeof doc.title === "string" ? doc.title : "",
      number: hasNumber ? String(rawNumber) : unassigned.text,
      numberKind: hasNumber ? null : unassigned.kind,
      counterparty: counterparty,
      amount: typeof rawAmount === "number" ? rawAmount.toLocaleString("ko-KR") + "원" : _EMPTY_VALUE,
      date: typeof rawDate === "string" && rawDate !== "" ? rawDate : fallbackDate || _EMPTY_VALUE,
    };
  }

  // ===== 행 렌더 =====
  // 문서 제목·상대방명은 외부 입력이다 — 전부 textContent 로 넣고 데이터로 마크업을 조립하지 않는다.

  function _appendSpan(parent: HTMLElement, className: string, text: string): HTMLElement {
    var span = document.createElement("span");
    span.className = className;
    span.textContent = text;
    parent.appendChild(span);
    return span;
  }

  function _createRowElement(row: DocumentRow): HTMLElement {
    var item = document.createElement("li");
    item.className = "business-documents-row";
    item.setAttribute("data-document-id", row.id);
    item.setAttribute("data-document-type", row.type);
    // 행은 상세로 들어가는 버튼이다. 마우스만 배선하면 키보드 사용자에게는 목록이 막힌다.
    item.setAttribute("role", "button");
    item.setAttribute("tabindex", "0");
    item.setAttribute("aria-selected", row.id === _selectedId ? "true" : "false");
    item.addEventListener("click", function () {
      void _selectDocument(row.id);
    });
    item.addEventListener("keydown", function (event) {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      // Space 의 기본 동작은 스크롤이다 — 막지 않으면 화면이 튀면서 선택된다.
      event.preventDefault();
      void _selectDocument(row.id);
    });

    var main = document.createElement("div");
    main.className = "business-documents-row-main";
    var pill = _appendSpan(main, "ax-status-pill", row.stateLabel);
    // 미지 상태에는 data-state 를 붙이지 않는다 — 없는 변형을 주장하느니 기본 pill 이 낫다.
    if (row.stateKey !== "") pill.setAttribute("data-state", row.stateKey);
    _appendSpan(main, "business-documents-row-type", row.typeLabel);
    _appendSpan(main, "business-documents-row-title", row.title);
    item.appendChild(main);

    var meta = document.createElement("div");
    meta.className = "business-documents-row-meta";
    var number = _appendSpan(meta, "business-documents-row-number", row.number);
    if (row.numberKind !== null) number.setAttribute("data-unassigned", row.numberKind);
    _appendSpan(meta, "business-documents-row-counterparty", row.counterparty);
    _appendSpan(meta, "business-documents-row-amount", row.amount);
    _appendSpan(meta, "business-documents-row-date", row.date);
    item.appendChild(meta);

    return item;
  }

  // ===== 패널 상태 =====

  function _setPanelState(state: PanelState) {
    _setHidden(_byId("business-documents-loading"), state !== "loading");
    _setHidden(_byId("business-documents-empty"), state !== "empty");
    _setHidden(_byId("business-documents-error"), state !== "error");
    _setHidden(_byId("business-documents-list"), state !== "list");
  }

  function _renderError(status: number | null) {
    _loadFailed = true;
    _setPanelState("error");
    var message = status !== null && status >= 400 && status < 500 ? _MSG_ERROR_DATA : _MSG_ERROR_FETCH;
    _setText(_byId("business-documents-error"), message);
    _announce(message);
  }

  // 0건 문구는 Phase 1 의 .ui-empty 3단 노드 텍스트만 교체한다 — 노드를 새로 만들지 않는다.
  function _applyEmptyText(): void {
    // 전체도 0건이면 필터 탓이 아니다. "전체는 있는데 내 것이 0건" 일 때만 범위를 안내한다.
    var scoped = _isPersonalScope() && _actorId() !== null && _documents.length > 0;
    _setText(_byId("business-documents-empty-title"), scoped ? _EMPTY_MINE_TITLE : _EMPTY_ALL_TITLE);
    _setText(_byId("business-documents-empty-sub"), scoped ? _EMPTY_MINE_SUB : _EMPTY_ALL_SUB);
    _announce(scoped ? "내가 만든 문서가 0건입니다." : "문서가 0건입니다.");
  }

  // 0건은 정상 경로다 — 빈 상태를 켜고 오류 상태는 꺼진 채로 끝난다.
  function _render(documents: BusinessDocumentRecord[]) {
    _setText(_byId("business-documents-count-pill"), documents.length + "건");
    const list = _byId("business-documents-list");
    if (list) list.innerHTML = "";
    _rowElements = [];
    if (documents.length === 0) {
      _setPanelState("empty");
      _applyEmptyText();
      return;
    }
    _setPanelState("list");
    if (list) {
      _sortDocuments(documents).forEach(function (doc) {
        var element = _createRowElement(_projectDocumentRow(doc));
        // querySelectorAll 대신 만든 순서를 들고 있는다 — 선택 표시 갱신에 DOM 재조회가 필요 없다.
        _rowElements.push(element);
        list.appendChild(element);
      });
    }
    _announce(documents.length + "건을 불러왔습니다.");
  }

  // 범위 변경은 이미 받은 배열의 재투영이다 — 서버를 다시 부르지 않는다.
  function _renderCurrent(): void {
    if (_loadFailed) return;
    _render(_visibleDocuments());
  }

  function _syncRowSelection(): void {
    _rowElements.forEach(function (element) {
      var id = element.getAttribute("data-document-id");
      element.setAttribute("aria-selected", id !== null && id === _selectedId ? "true" : "false");
    });
  }

  // ===== 상세 투영 =====
  // 아래 네 칸(만든 사람·승인·반려 이력·출처)은 값이 객체 또는 객체 배열이다.
  // textContent 에 그대로 넣으면 [object Object] 가 렌더되고, 노드 존재만 보는 테스트는 통과한다.

  function _dateOf(value: unknown): string {
    // ISO 8601 의 앞 10자가 곧 YYYY-MM-DD 다. 로케일 포맷터는 실행 환경에 묶여 쓰지 않는다.
    return typeof value === "string" && value.length >= 10 ? value.slice(0, 10) : _EMPTY_VALUE;
  }

  // ActorStub 의 표시 이름. 같은 displaySnapshot 의 excerpt 는 작성자 이메일로 시작하고
  // (identity-contracts.ts:118-124) 목록이 평면 가시라, 그리는 순간 팀 전원에게 노출된다.
  function _actorLabel(value: unknown): string {
    if (!_isRecord(value)) return _EMPTY_VALUE;
    var snapshot = _isRecord(value.displaySnapshot) ? value.displaySnapshot : null;
    if (snapshot !== null && typeof snapshot.label === "string" && snapshot.label !== "") {
      return snapshot.label;
    }
    return typeof value.actor === "string" && value.actor !== "" ? value.actor : _EMPTY_VALUE;
  }

  // 출처는 유형과 개수까지다. 각 출처의 위치 문자열은 방 id 이고, 방 가시성 판정은 문서 생성
  // 시점에만 걸린다(document-ops.ts:103-113) — 읽기 경로에는 없어서 그대로 그리면 비가시 방의
  // id 가 전원에게 보인다. 정책을 바꾸지 않으므로 뷰가 정책보다 좁게 간다.
  function _sourceRefsText(value: unknown): string {
    if (!Array.isArray(value) || value.length === 0) return _EMPTY_VALUE;
    var types: string[] = [];
    (value as unknown[]).forEach(function (ref) {
      var rawType = _isRecord(ref) && typeof ref.sourceType === "string" ? ref.sourceType : "";
      if (rawType === "") return;
      var label = _SOURCE_TYPE_LABELS[rawType] || rawType;
      // 같은 유형이 여러 건이면 유형은 한 번만 적는다 — "채팅, 채팅, 채팅 3건" 은 읽히지 않는다.
      if (types.indexOf(label) < 0) types.push(label);
    });
    return (types.length > 0 ? types.join(", ") + " " : "") + value.length + "건";
  }

  function _setUnassigned(element: HTMLElement | null, kind: UnassignedKind | null): void {
    if (!element) return;
    if (kind === null) element.removeAttribute("data-unassigned");
    else element.setAttribute("data-unassigned", kind);
  }

  // ===== 반려 이력 =====

  // 문서함 표시 상한(D-REJECT-CAP 의 표시 축). 저장 건수는 캡하지 않는다(contracts.ts:71-76) —
  // 평면 가시 정책이라 한 문서의 비대한 이력이 전원의 목록 응답에 실리므로, 화면에서만 자른다.
  var _REJECTION_HISTORY_DISPLAY_LIMIT = 5;

  // contracts.ts:702 isSameRejectionEntry 의 뷰 사본. rejectedAt + reason 키로 판별하고
  // 깊은 비교는 하지 않는다 — 브라우저 classic script 는 서버 모듈을 require 할 수 없다.
  // 동치는 test/business-documents-number-key-contract.test.ts 가 고정한다.
  function _isSameRejectionEntryView(
    a: Record<string, unknown> | null | undefined,
    b: Record<string, unknown> | null | undefined,
  ): boolean {
    if (!_isRecord(a) || !_isRecord(b)) return false;
    return a.rejectedAt === b.rejectedAt && a.reason === b.reason;
  }

  // 이력(rejectionHistory) + 이력에 없는 미해소 반려(rejection)를 중복 없이 합쳐 그린다.
  // Phase 4 이후의 거절은 거절 시점에 이미 이력에 있으므로, 단순히 이어붙이면 같은 항목이
  // 두 번 보인다. 재제출 전인 기존 반려 문서만 rejection 이 이력에 없어 여기서 덧붙는다.
  function _renderRejectionHistory(doc: BusinessDocumentRecord): void {
    var container = _byId("business-documents-field-rejection");
    // 리셋은 분기보다 먼저, 무조건 돈다 — 그러지 않으면 이전 문서의 반려 칸이 남는다.
    if (container) container.innerHTML = "";

    var history: Record<string, unknown>[] = Array.isArray(doc.rejectionHistory)
      ? (doc.rejectionHistory as unknown[]).filter(_isRecord)
      : [];
    var merged = history.slice();
    if (_isRecord(doc.rejection) && !history.some(function (entry) {
      return _isSameRejectionEntryView(entry, doc.rejection as Record<string, unknown>);
    })) {
      merged.push(doc.rejection as Record<string, unknown>);
    }

    if (merged.length === 0) {
      _setText(container, "없음");
      return;
    }
    if (!container) return;

    // 정렬 = 저장 append 순서의 역순(최신이 먼저). rejectedAt 문자열 비교로 구현하지 않는다 —
    // ISO_TIMESTAMP_RE 가 Z 와 ±HH:MM 오프셋을 모두 허용해(contracts.ts:39) 오프셋이 섞이면
    // 사전식 비교가 틀린다. 역순 인덱싱이면 비교 자체가 필요 없다.
    var newestFirst = merged.slice().reverse();
    var shown = newestFirst.slice(0, _REJECTION_HISTORY_DISPLAY_LIMIT);
    var omitted = newestFirst.length - shown.length;

    var list = document.createElement("ul");
    shown.forEach(function (entry) {
      var item = document.createElement("li");
      item.textContent = (typeof entry.reason === "string" ? entry.reason : _EMPTY_VALUE)
        + " (" + _dateOf(entry.rejectedAt) + ")";
      list.appendChild(item);
    });
    if (omitted > 0) {
      var more = document.createElement("li");
      more.className = "business-documents-rejection-history-more";
      more.textContent = "외 " + omitted + "건";
      list.appendChild(more);
    }
    container.appendChild(list);
  }

  // 미등록 id 는 원문 id 를 그대로 보여준다. business-document-card.ts 의 _missingLabel 이
  // "매핑이 없으면 원문 그대로" 를 이미 관용으로 세웠고, 여기서 _EMPTY_VALUE 로 접으면
  // "발행 법인이 없다" 와 "이 id 를 모른다" 가 한 표기로 붕괴한다.
  function _issuerLabel(value: unknown): string {
    if (typeof value !== "string" || value === "") return _EMPTY_VALUE;
    return Object.prototype.hasOwnProperty.call(_ISSUER_LEGAL_NAMES, value)
      ? _ISSUER_LEGAL_NAMES[value]
      : value;
  }

  // ===== 상세 패널 =====

  // 상세도 배타다. 선택 힌트는 hidden 없이 초기 렌더되는 것이 계약이므로(선택 전 기본 가시)
  // 문서를 고르는 순간 여기서 숨긴다 — 숨기지 않으면 "아직 선택된 문서가 없습니다" 가
  // 상세 내용 위에 그대로 남는다.
  function _setDetailState(state: DetailState): void {
    _setHidden(_byId("business-documents-detail-empty"), state !== "hint");
    _setHidden(_byId("business-documents-detail-error"), state !== "error");
    _setHidden(_byId("business-documents-detail-content"), state !== "content");
  }

  // ===== 발행 산출물 =====
  // 문서함은 **보장된** 도달 표면이다: 실시간 카드가 한 번도 오지 않아도 여기서 발행본에 닿는다.

  // business-document-card.ts 의 다운로드 앵커 조립을 그대로 미러한다: 새 창 + noopener.
  // 문서 데이터로 마크업을 조립하지 않는다 — 문구는 textContent, href 는 판정자가 만든 값이다.
  function _appendOutputLink(parent: HTMLElement, kind: string, href: string, text: string): void {
    var link = document.createElement("a");
    link.className = "ui-btn ax-button ax-button--link";
    link.setAttribute("href", href);
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener");
    link.setAttribute("data-output-kind", kind);
    link.textContent = text;
    parent.appendChild(link);
  }

  /**
   * "발행본인가" 와 "URL 이 무엇인가" 의 판정자는 business-document-card.ts 의 publishedOutput
   * 하나뿐이다. 여기서 술어를 다시 쓰면 카드와 문서함이 갈라지고, 그 갈림의 실패 모드가 곧
   * 초안 워터마크 PDF 를 정식 발행본인 양 내주는 것이다(초안 산출물은 `<documentId>-draft-<hash>`
   * 라는 **다른 파일**이라 이 URL 로는 올 수 없다 — publish-ops.ts resolveOutputBaseName).
   *
   * 조회 0회 · 비동기 0개다. 그래서 _selectDocument 의 stale-response 레이스가 여기서는 생기지
   * 않는다. 카드 스크립트가 안 실려도 throw 하지 않고 섹션만 접는다 — 판정을 대신하지 않는 것이
   * 이 갈림을 없애는 유일한 방법이다.
   */
  function _renderPublishedOutput(doc: BusinessDocumentRecord): void {
    var section = _byId("business-documents-detail-output");
    var links = _byId("business-documents-detail-output-links");
    // 열 칸 재대입과 같은 계약이다 — 먼저 비우지 않으면 이전 문서의 발행본 링크가 남는다.
    if (links) links.innerHTML = "";

    var output = typeof BusinessDocumentCard !== "undefined"
      ? BusinessDocumentCard.publishedOutput.resolve(doc)
      : null;
    if (links === null || output === null) {
      // 빈 섹션을 남기지 않는다. 남기면 "발행 산출물" 라벨만 뜬 칸이 발행되지 않은 문서에서도
      // 보이고, "링크 0개" 음성 검증은 그대로 통과한다.
      _setHidden(section, true);
      return;
    }

    _appendOutputLink(links, "pdf", output.pdfHref, _LABEL_OUTPUT_PDF);
    _appendOutputLink(links, "html", output.htmlHref, _LABEL_OUTPUT_HTML);
    _setHidden(section, false);
  }

  function _renderDocumentDetail(doc: BusinessDocumentRecord): void {
    // 목록과 같은 투영을 쓴다. 두 표면이 각자 계산하면 같은 문서의 금액·번호가 갈라진다.
    var row = _projectDocumentRow(doc);

    var pill = _byId("business-documents-detail-state-pill");
    _setText(pill, row.stateLabel);
    if (pill) {
      if (row.stateKey !== "") pill.setAttribute("data-state", row.stateKey);
      else pill.removeAttribute("data-state");
    }

    // 열 칸을 매번 전부 재대입한다. approval 과 rejection 은 optional 이라, 부재를 덮어쓰지
    // 않으면 승인 문서를 본 뒤 초안을 골랐을 때 이전 승인자 이름이 그대로 남는다.
    _setText(_byId("business-documents-field-type"), row.typeLabel);
    var number = _byId("business-documents-field-number");
    _setText(number, row.number);
    _setUnassigned(number, row.numberKind);
    _setText(_byId("business-documents-field-counterparty"), row.counterparty);
    _setText(_byId("business-documents-field-amount"), row.amount);
    _setText(_byId("business-documents-field-date"), row.date);
    // 법인의 사업자번호·대표자·주소·계좌는 미추적 로컬 파일이 소유하고 문서 레코드에 없다.
    _setText(_byId("business-documents-field-issuer"), _issuerLabel(doc.issuerEntityId));

    _setText(_byId("business-documents-field-created-by"), _actorLabel(doc.createdBy));
    _setText(_byId("business-documents-field-approval"),
      _isRecord(doc.approval)
        ? _actorLabel(doc.approval) + " · " + _dateOf(doc.approval.approvedAt)
        : _EMPTY_VALUE);
    // 이력(rejectionHistory) + 이력에 없는 미해소 반려(rejection)를 중복 없이 합쳐 보여준다.
    // 재제출 전인 기존 반려 문서는 rejection 만 있고 이력엔 없어 여기서 합쳐진다(Phase 5).
    _renderRejectionHistory(doc);
    _setText(_byId("business-documents-field-source-refs"), _sourceRefsText(doc.sourceRefs));
    _renderPublishedOutput(doc);

    _setDetailState("content");
  }

  // 여백 클릭 = 닫기(D-INBOX-BACKDROP-CLOSE, 사용자 결정 2026-08-25). 별도 닫기 상태를 만들지
  // 않는다 — 선택 상태(_selectedId)가 곧 패널 표시 조건이므로 선택 해제가 곧 닫기다.
  // _selectDocument 의 stale-response 가드(_selectedId !== id)가 해제 중 늦게 도착한 상세 응답도
  // 함께 버린다.
  function _clearSelection(): void {
    if (_selectedId === null) return;
    _selectedId = null;
    _syncRowSelection();
    _setText(_byId("business-documents-detail-state-pill"), "미선택");
    _setDetailState("hint");
  }

  async function _selectDocument(id: string): Promise<void> {
    if (id === "") return;
    _selectedId = id;
    _syncRowSelection();
    _setDetailState("loading");
    _setText(_byId("business-documents-detail-state-pill"), "불러오는 중");
    try {
      // 목록에서 받은 id 를 그대로 쓴다. 저장소의 validateSlug 가 대소문자를 접기 때문에
      // 요청 id 와 레코드 id 가 다르면 404 다(routes-draft.ts:80-83) — 정규화하면 안 된다.
      const res = await fetch(_API_LIST + encodeURIComponent(id), { credentials: "same-origin" });
      if (!res.ok) throw new Error("Business document detail request failed");
      const payload: unknown = await res.json();
      if (!_isRecord(payload) || !_isRecord(payload.document)) {
        throw new Error("Invalid business document detail payload");
      }
      // 늦게 도착한 응답이 그 사이 바뀐 선택을 덮지 않는다.
      if (_selectedId !== id) return;
      _renderDocumentDetail(payload.document);
    } catch {
      if (_selectedId !== id) return;
      _setText(_byId("business-documents-detail-state-pill"), "불러오기 실패");
      // 목록은 건드리지 않는다 — 상세 하나가 실패했다고 목록까지 사라지면 복구 경로가 없다.
      _setDetailState("error");
    }
  }

  // ===== 조회 =====
  // 정적 서빙 스텁처럼 /api/business-documents/ 를 모르는 환경에서도 throw 하지 않아야 한다
  // (nav-shell-browser-smoke 의 클릭-도달 검증이 함께 무너진다). 모든 경로를 catch 가 덮는다.
  async function _load(): Promise<void> {
    try {
      _setPanelState("loading");
      _announce(_MSG_LOADING);
      const res = await fetch(_API_LIST, { credentials: "same-origin" });
      if (!res.ok) {
        _renderError(res.status);
        return;
      }
      _documents = _documentsFromUnknown(await res.json());
      _loadFailed = false;
      _renderCurrent();
    } catch {
      // 네트워크 실패 · JSON 파싱 실패 · 응답 형태 불일치
      _renderError(null);
    }
  }

  // ===== 초기화 =====

  // 로그인 신원이 없으면 "내 문서" 가 의미를 잃는다. 선례: proposal-review.ts:128-135.
  async function _checkPersonalEnabled(): Promise<boolean> {
    try {
      const response = await fetch(_API_ME, { credentials: "same-origin" });
      return response.ok;
    } catch {
      return false;
    }
  }

  async function _mountScopeSwitch(): Promise<void> {
    var personalEnabled = await _checkPersonalEnabled();
    if (personalEnabled) _seedDefaultScope();
    _scopes = _readPersistedDocumentScopes(personalEnabled);
    var mount = _byId("business-documents-scope-switch-mount");
    if (mount && typeof ScopeSwitch !== "undefined") {
      mount.textContent = "";
      mount.appendChild(ScopeSwitch.create({
        viewId: _VIEW_SCOPE_ID,
        personalEnabled: personalEnabled,
        selectionMode: "single",
        labels: { team: "전체 문서", personal: "내 문서" },
        ariaLabel: "문서 범위",
        onChange: function (payload) {
          _scopes = payload.scopes;
          _renderCurrent();
        },
      }));
    }
  }

  function _wireRefreshButton(): void {
    var button = _byId("business-documents-refresh-button");
    if (button) {
      button.addEventListener("click", function () {
        void _load();
      });
    }
  }

  // 상세 패널 바깥 여백 클릭은 닫기다(D-INBOX-BACKDROP-CLOSE — decisions.md, 무시하지 않는다).
  // "여백" 판정은 배제식이다: 상세 패널 안(닫으면 안 됨)·목록 행(선택 동작)·상호작용 요소
  // (새로고침·범위 스위치·다운로드 링크 등)는 여백이 아니고, 그 밖의 뷰 영역 클릭만 해제한다.
  function _wireBackdropClose(): void {
    var root = _byId("view-business-documents");
    if (!root) return;
    root.addEventListener("click", function (event) {
      var target: unknown = event && event.target;
      // instanceof Element 를 쓰지 않는다 — 이 뷰 스크립트는 jsdom 없는 node 하네스(미니 DOM,
      // business-documents-inbox-page.test.ts)에서도 실행되므로 closest 유무로 요소를 판정한다.
      if (!target || typeof (target as Element).closest !== "function") return;
      var element = target as Element;
      if (element.closest("#business-documents-detail-panel")) return;
      if (element.closest(".business-documents-row")) return;
      if (element.closest("button, a, input, select, textarea, label, [role=\"button\"]")) return;
      _clearSelection();
    });
  }

  function initBusinessDocumentInbox(): Promise<void> {
    if (_initPromise !== null) return _initPromise;
    _initPromise = (async function () {
      _wireRefreshButton();
      _wireBackdropClose();
      await _mountScopeSwitch();
      await _load();
    })();
    return _initPromise;
  }

  // 전역 노출 (work/home.ts 의 initWorkHome 패턴 참고)
  if (typeof window !== "undefined") {
    (window as Window & {
      BusinessDocumentInbox?: {
        init(): Promise<void>;
        load(): Promise<void>;
        stateLabels: Record<string, string>;
        typeLabels: Record<string, string>;
        numberKeyByType: Record<string, string>;
        isSameRejectionEntry(
          a: Record<string, unknown> | null | undefined,
          b: Record<string, unknown> | null | undefined,
        ): boolean;
      };
    }).BusinessDocumentInbox = {
      init: initBusinessDocumentInbox,
      load: _load,
      // contracts.ts 상수/술어의 프런트 사본. 갈라지면 문서번호 열이 전 문서에서 조용히 비거나
      // 반려 이력이 중복 표시되므로 test/business-documents-number-key-contract.test.ts 가
      // 여기를 읽어 동치를 단언한다.
      stateLabels: _STATE_LABELS,
      typeLabels: _TYPE_LABELS,
      numberKeyByType: _NUMBER_KEY_BY_TYPE,
      isSameRejectionEntry: _isSameRejectionEntryView,
    };
  }

  // view-enter 디스패처에 등록
  if (typeof router !== "undefined") {
    router.registerViewEnter(function (viewName) {
      if (viewName !== "business-documents") return;
      initBusinessDocumentInbox();
    });
  }
})();
